<template>
  <v-app>
    <v-container fluid>
      <v-row v-if="$insertAccess($route.fullPath)">
        <v-col cols="12">
          <v-expansion-panels v-model="panel">
            <v-expansion-panel>
              <v-expansion-panel-title color="blue-grey-lighten-4" class="panel-header">
                <v-icon class="me-2">mdi-clock-time-four-outline</v-icon>
                Shift-Wise Attendance Report
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <v-form @submit.prevent="loadReport" ref="filterForm">
                  <v-row class="py-3 px-3" align="end">
                  <v-col cols="12" sm="6" md="3">
                    <v-date-input
                      input-format="dd/mm/yyyy"
                      v-model="filters.from_date"
                      label="From Date"
                      variant="outlined"
                      density="compact"
                      bg-color="grey-lighten-4"
                      prepend-icon=""
                      prepend-inner-icon="mdi-calendar"
                      hide-details="auto"
                      required
                    />
                  </v-col>
                  <v-col cols="12" sm="6" md="3">
                    <v-date-input
                      input-format="dd/mm/yyyy"
                      v-model="filters.to_date"
                      label="To Date"
                      variant="outlined"
                      density="compact"
                      bg-color="grey-lighten-4"
                      prepend-icon=""
                      prepend-inner-icon="mdi-calendar"
                      hide-details="auto"
                      required
                    />
                  </v-col>
                  <v-col cols="12" sm="6" md="3">
                    <v-autocomplete
                      v-model="filters.shift_id"
                      :items="shifts"
                      :item-title="(item) => `${item.shift_name || item.name || ''}${item.code ? ` (${item.code})` : ''}`"
                      item-value="id"
                      label="Shift (Optional)"
                      variant="outlined"
                      density="compact"
                      bg-color="grey-lighten-4"
                      clearable
                      hide-details="auto"
                      :loading="shiftsLoading"
                      placeholder="Select shift"
                    />
                  </v-col>
                  <v-col cols="12" sm="6" md="3">
                    <v-autocomplete
                      v-model="filters.employee_id"
                      :items="employees"
                      :item-title="item => `${item.name}-(${item.code})` ?? 'N/A'"
                      item-value="id"
                      label="Employee (Optional)"
                      variant="outlined"
                      density="compact"
                      bg-color="grey-lighten-4"
                      clearable
                      hide-details="auto"
                      :loading="employeesLoading"
                      placeholder="Search employee"
                    />
                  </v-col>
                  <v-col cols="12" sm="6" md="3" class="d-flex align-end">
                    <v-btn
                      type="submit"
                      color="primary"
                      variant="flat"
                      :loading="loading"
                      prepend-icon="mdi-magnify"
                      density="compact"
                      height="40"
                    >
                      Search
                    </v-btn>
                  </v-col>
                  </v-row>
                </v-form>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>

          <!-- Report Table -->
          <v-expansion-panels v-model="reportPanel" class="mt-2">
            <v-expansion-panel>
              <v-expansion-panel-title
                color="blue-grey-lighten-4"
                class="panel-header"
                expand-icon="mdi-chevron-down"
                >
                <v-icon class="me-2">mdi-table</v-icon>
                Report Data
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <v-row class="py-2">
                  <v-col md="4" cols="12">
                    <v-text-field
                      v-model="search"
                      append-inner-icon="mdi-magnify"
                      label="Search"
                      single-line
                      hide-details
                      density="compact"
                      variant="outlined"
                      bg-color="grey-lighten-4"
                      class="pl-2"
                      clearable
                    ></v-text-field>
                  </v-col>

                  <v-col md="5" cols="12">
                    <div class="d-flex align-center flex-column">
                      <v-btn-group
                        v-if="reportData.length > 0"
                        variant="outlined"
                        density="compact"
                        size="x-small"
                        divided
                      >
                        <v-btn
                          prepend-icon="mdi mdi-download"
                          variant="flat"
                          color="success"
                          @click="exportToExcel"
                          :disabled="!reportData.length"
                          >EXCEL</v-btn
                        >
                        <v-btn
                          prepend-icon="mdi mdi-download"
                          variant="flat"
                          color="red"
                          @click="exportToPDF"
                          :disabled="!reportData.length"
                          >PDF</v-btn
                        >
                      </v-btn-group>
                    </div>
                  </v-col>

                  <v-col md="3" cols="12"></v-col>
                </v-row>

                <v-progress-linear
                  v-if="loading"
                  indeterminate
                  color="primary"
                  class="mb-4"
                />

                <v-alert
                  v-if="!loading && filteredReportData.length === 0"
                  type="info"
                  variant="tonal"
                >
                  No shift-wise attendance data found for the selected criteria.
                </v-alert>

                <div v-if="filteredReportData.length">
                  <v-table density="comfortable" class="text-caption table-bordered">
                    <thead>
                      <tr>
                        <th># SL</th>
                        <th>Date</th>
                        <th style="min-width: 150px;text-align: center;">Shift</th>
                        <th style="text-align: center;">Total Empl.</th>
                        <th style="text-align: center;">Present</th>
                        <th style="text-align: center;">Absent</th>
                        <th style="text-align: center;">Late</th>
                        <th style="text-align: center;">Early</th>
                        <th style="text-align: center;">Late & Early</th>
                        <th style="text-align: center;">On Leave</th>
                        <th style="text-align: center;">Weekend/Holiday</th>
                        <th style="text-align: center;">Punch Missing</th>
                        <th style="text-align: center;">OT (hrs)</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(row, index) in filteredReportData" :key="`${row.date}-${row.shift?.shift_id || 'no-shift'}-${index}`">
                        <td>{{ index + 1 }}</td>
                        <td>{{ formatDateView(row.date) }}</td>
                        <td>{{ formatShift(row.shift) }}</td>
                        <td style="text-align: center;">{{ formatNumber(row.total_employees) }}</td>
                        <td style="text-align: center;">{{ formatNumber(row.present) }}</td>
                        <td style="text-align: center;">{{ formatNumber(row.absent) }}</td>
                        <td style="text-align: center;">{{ formatNumber(row.late) }}</td>
                        <td style="text-align: center;">{{ formatNumber(row.early) }}</td>
                        <td style="text-align: center;">{{ formatNumber(row.late_and_early) }}</td>
                        <td style="text-align: center;">{{ formatNumber(row.on_leave) }}</td>
                        <td style="text-align: center;">{{ formatNumber(row.weekend_holiday) }}</td>
                        <td style="text-align: center;">{{ formatNumber(row.punch_missing) }}</td>
                        <td style="text-align: center;">{{ formatNumber(row.total_ot_hours, 2) }}</td>
                      </tr>
                    </tbody>
                    <tfoot v-if="filteredReportData.length > 0">
                      <tr style="font-weight: bold; color: #000000;">
                        <td colspan="3" style="text-align: right; padding: 12px;">Total:</td>
                        <td style="text-align: center; padding: 12px;">{{ formatNumber(filteredTotals.total_employees) }}</td>
                        <td style="text-align: center; padding: 12px;">{{ formatNumber(filteredTotals.present) }}</td>
                        <td style="text-align: center; padding: 12px;">{{ formatNumber(filteredTotals.absent) }}</td>
                        <td style="text-align: center; padding: 12px;">{{ formatNumber(filteredTotals.late) }}</td>
                        <td style="text-align: center; padding: 12px;">{{ formatNumber(filteredTotals.early) }}</td>
                        <td style="text-align: center; padding: 12px;">{{ formatNumber(filteredTotals.late_and_early) }}</td>
                        <td style="text-align: center; padding: 12px;">{{ formatNumber(filteredTotals.on_leave) }}</td>
                        <td style="text-align: center; padding: 12px;">{{ formatNumber(filteredTotals.weekend_holiday) }}</td>
                        <td style="text-align: center; padding: 12px;">{{ formatNumber(filteredTotals.punch_missing) }}</td>
                        <td style="text-align: center; padding: 12px;">{{ formatNumber(filteredTotals.total_ot_hours, 2) }}</td>
                      </tr>
                    </tfoot>
                  </v-table>
                </div>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </v-container>
  </v-app>
</template>

<script setup>
import { ref, reactive, computed, onMounted, getCurrentInstance } from 'vue'
import { toast } from 'vue3-toastify'
import { useStore } from 'vuex'
import { formatDateView, formatDateDash } from "@/plugins/helpers";


const store = useStore()
const instance = getCurrentInstance()
const appContext = instance?.appContext

// Loading states
const loading = ref(false)
const employeesLoading = ref(false)
const shiftsLoading = ref(false)
const panel = ref([0])
const reportPanel = ref([0])
const search = ref('')

// Filters
const filters = reactive({
  from_date: '',
  to_date: '',
  shift_id: null,
  employee_id: null
})

// Data
const reportData = ref([])
const employees = ref([])
const shifts = ref([])

const formatEmployeeLabel = item => {
  if (!item) return ''
  return `${item.name || ''}${item.employee_code ? ` - ${item.employee_code}` : ''}`
}

const formatNumber = (value, fraction = 0) => {
  if (value === undefined || value === null || isNaN(value)) return '-'
  return Number(value).toLocaleString(undefined, {
    minimumFractionDigits: fraction,
    maximumFractionDigits: fraction
  })
}

const formatShift = (shift) => {
  if (!shift) return '-'
  if (typeof shift === 'string') return shift
  if (shift.shift_name) {
    const timeStr = shift.in_time && shift.out_time 
      ? ` (${formatTime(shift.in_time)} - ${formatTime(shift.out_time)})`
      : ''
    return `${shift.shift_name}${timeStr}`
  }
  if (shift.name) return shift.name
  if (shift.in_time && shift.out_time) {
    return `${formatTime(shift.in_time)} - ${formatTime(shift.out_time)}`
  }
  return '-'
}

const formatTime = (timeStr) => {
  if (!timeStr || timeStr === '-') return '-'
  const timeParts = timeStr.split(':')
  if (timeParts.length < 2) return timeStr
  const hours = parseInt(timeParts[0], 10)
  const minutes = timeParts[1]
  if (isNaN(hours)) return timeStr
  const period = hours >= 12 ? 'PM' : 'AM'
  const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours
  return `${displayHours}:${minutes} ${period}`
}

// Filtered report data based on search
const filteredReportData = computed(() => {
  if (!search.value) {
    return reportData.value
  }
  
  const searchLower = search.value.toLowerCase()
  return reportData.value.filter(row => {
    const date = formatDateView(row.date).toLowerCase()
    const shift = formatShift(row.shift).toLowerCase()
    return date.includes(searchLower) || shift.includes(searchLower)
  })
})

// Computed totals
const totals = computed(() => {
  return reportData.value.reduce(
    (acc, row) => {
      acc.total_employees += Number(row.total_employees) || 0
      acc.present += Number(row.present) || 0
      acc.absent += Number(row.absent) || 0
      acc.late += Number(row.late) || 0
      acc.early += Number(row.early) || 0
      acc.late_and_early += Number(row.late_and_early) || 0
      acc.on_leave += Number(row.on_leave) || 0
      acc.weekend_holiday += Number(row.weekend_holiday) || 0
      acc.punch_missing += Number(row.punch_missing) || 0
      acc.total_ot_hours += Number(row.total_ot_hours) || 0
      return acc
    },
    {
      total_employees: 0,
      present: 0,
      absent: 0,
      late: 0,
      early: 0,
      late_and_early: 0,
      on_leave: 0,
      weekend_holiday: 0,
      punch_missing: 0,
      total_ot_hours: 0
    }
  )
})

// Computed totals for filtered data
const filteredTotals = computed(() => {
  return filteredReportData.value.reduce(
    (acc, row) => {
      acc.total_employees += Number(row.total_employees) || 0
      acc.present += Number(row.present) || 0
      acc.absent += Number(row.absent) || 0
      acc.late += Number(row.late) || 0
      acc.early += Number(row.early) || 0
      acc.late_and_early += Number(row.late_and_early) || 0
      acc.on_leave += Number(row.on_leave) || 0
      acc.weekend_holiday += Number(row.weekend_holiday) || 0
      acc.punch_missing += Number(row.punch_missing) || 0
      acc.total_ot_hours += Number(row.total_ot_hours) || 0
      return acc
    },
    {
      total_employees: 0,
      present: 0,
      absent: 0,
      late: 0,
      early: 0,
      late_and_early: 0,
      on_leave: 0,
      weekend_holiday: 0,
      punch_missing: 0,
      total_ot_hours: 0
    }
  )
})

// Computed for company info
const getCurrentCompany = computed(() => {
  return store.getters.getCurrentCompany || {}
})

const getFileServerUrl = computed(() => {
  return store.getters.getFileServerUrl || ''
})



const setDefaultDates = () => {
  const today = new Date()
  const firstDay = new Date(today.getFullYear(), today.getMonth(), 1)
  const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0)
  filters.from_date = firstDay
  filters.to_date = lastDay
}

// Methods
const loadEmployees = async () => {
  employeesLoading.value = true
  try {
    const response = await store.dispatch('getEmployees')
    employees.value = response.data.data || []
  } catch (error) {
    employees.value = []
    toast.error(error.message || 'Failed to load employees')
  } finally {
    employeesLoading.value = false
  }
}

const loadShifts = async () => {
  shiftsLoading.value = true
  try {
    if (store.getters.getShiftList && store.getters.getShiftList.length > 0) {
      shifts.value = store.getters.getShiftList
    } else {
      await store.dispatch('getShiftList', {})
      shifts.value = store.getters.getShiftList || []
    }
  } catch (error) {
    shifts.value = []
    toast.error(error.message || 'Failed to load shifts')
  } finally {
    shiftsLoading.value = false
  }
}

const loadReport = async () => {
  if (!filters.from_date || !filters.to_date) {
    toast.error('Please select both From Date and To Date')
    return
  }

  loading.value = true
  try {
    const params = {
      from_date: formatDateDash(filters.from_date),
      to_date: formatDateDash(filters.to_date),
      employee_id: filters.employee_id || undefined,
      shift_id: filters.shift_id || undefined
    }

    const response = await store.dispatch('getShiftWiseAttendanceSummaryReport', params)

    if (response.success) {
      reportData.value = response.data || []
      if (reportData.value.length === 0) {
        toast.info('No data found for the selected criteria')
      }
    } else {
      toast.error(response.message || 'Failed to load report')
      reportData.value = []
    }
  } catch (error) {
    toast.error(error.response?.data?.message || error.message || 'Failed to load report')
    reportData.value = []
  } finally {
    loading.value = false
  }
}

const resetFilters = () => {
  setDefaultDates()
  filters.shift_id = null
  filters.employee_id = null
  reportData.value = []
}

// Export to Excel
const exportToExcel = () => {
  if (!reportData.value.length) {
    toast.warning('No data to export')
    return
  }

  const data = [
    ['Shift-Wise Attendance Report'],
    [`From Date: ${formatDateView(filters.from_date)} to ${formatDateView(filters.to_date)}`]
  ]
  
  if (filters.shift_id) {
    const selectedShift = shifts.value.find(s => s.id === filters.shift_id)
    if (selectedShift) {
      data.push([`Shift: ${selectedShift.shift_name || selectedShift.name || ''}${selectedShift.code ? ` (${selectedShift.code})` : ''}`])
    }
  }

  if (filters.employee_id) {
    const selectedEmployee = employees.value.find(e => e.id === filters.employee_id)
    if (selectedEmployee) {
      data.push([`Employee: ${selectedEmployee.name || ''}${selectedEmployee.employee_code ? ` (${selectedEmployee.employee_code})` : ''}`])
    }
  }
  
  data.push([], ['# SL', 'Date', 'Shift', 'Total Empl.', 'Present', 'Absent', 'Late', 'Early', 'Late & Early', 'On Leave', 'Weekend/Holiday', 'Punch Missing', 'OT (hrs)'], [])

  // Add data rows
  reportData.value.forEach((row, index) => {
    data.push([
      index + 1,
      formatDateView(row.date),
      formatShift(row.shift),
      formatNumber(row.total_employees),
      formatNumber(row.present),
      formatNumber(row.absent),
      formatNumber(row.late),
      formatNumber(row.early),
      formatNumber(row.late_and_early),
      formatNumber(row.on_leave),
      formatNumber(row.weekend_holiday),
      formatNumber(row.punch_missing),
      formatNumber(row.total_ot_hours, 2)
    ])
  })

  // Add totals row
  if (reportData.value.length > 0) {
    data.push([])
    data.push([
      '', '', 'Total:',
      formatNumber(totals.value.total_employees),
      formatNumber(totals.value.present),
      formatNumber(totals.value.absent),
      formatNumber(totals.value.late),
      formatNumber(totals.value.early),
      formatNumber(totals.value.late_and_early),
      formatNumber(totals.value.on_leave),
      formatNumber(totals.value.weekend_holiday),
      formatNumber(totals.value.punch_missing),
      formatNumber(totals.value.total_ot_hours, 2)
    ])
  }

  const proxy = appContext?.config?.globalProperties
  if (proxy?.$exportTableToExcel && proxy?.$fileDate) {
    proxy.$exportTableToExcel(data, 'shift_wise_attendance_report_' + proxy.$fileDate())
  }
}

// Export to PDF
const exportToPDF = async () => {
  if (!reportData.value.length) {
    toast.warning('No data to export')
    return
  }

  const company = getCurrentCompany.value
  const fileServerUrl = getFileServerUrl.value
  const selectedShift = filters.shift_id 
    ? shifts.value.find(s => s.id === filters.shift_id)
    : null
  const selectedEmployee = filters.employee_id 
    ? employees.value.find(e => e.id === filters.employee_id)
    : null

  let printWindow = window.open('', 'PRINT', `width=${screen.width}, height=${screen.height}, left=0, top=0`)
  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <meta http-equiv="X-UA-Compatible" content="ie=edge">
        <title>Shift-Wise Attendance Report</title>
        <link rel="preconnect" href="https://fonts.googleapis.com">
        <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
        <link href="https://fonts.googleapis.com/css2?family=Arimo:ital,wght@0,400..700;1,400..700&display=swap" rel="stylesheet">
        <link href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.8/dist/css/bootstrap.min.css" rel="stylesheet">
        <link href="https://cdn.jsdelivr.net/npm/@mdi/font@7.4.47/css/materialdesignicons.min.css" rel="stylesheet">
        <style>
          body { font-family: 'Arimo', sans-serif; margin: 0px; }
          .invoice-header { display:flex; justify-content:space-between; align-items:center; border-bottom:2px solid #eee; padding-bottom:10px; }
          .invoice-header img { max-height: 70px; max-width: 150px; }
          .table th, .table td { border: 1px solid #000; padding: 8px; vertical-align: middle; font-size: 11px; }
          .table th { background-color: #f0f0f0; color: #333; }
          .text-end { text-align: right; }
          .text-center { text-align: center; }
          h4 { font-size: 20px; color: #333; }
          p { font-size: 14px; color: #555; margin: 2px 0; }
          strong { font-weight: bold; color: #333; }
          @page { margin: 0.5in; size: letter; }
          .logo-container { display:flex; align-items:center; }
          .logo-container img { margin-left: 10px; }
          .table { width: 100%; border-collapse: collapse; table-layout: auto; }
          thead { display: table-header-group; }
          .totals-row { page-break-inside: avoid; }
          .totals-row td { font-weight: bold; background-color: #f5f5f5 !important; }
        </style>
      </head>
      <body>
        <div class="container-fluid position-relative">
          <div class="invoice-header">
            <div>
              <h4 class="text-uppercase">${company?.name || 'Company Name'}</h4>
              <p style="width:65%">${company?.address || ''}</p>
              <p><i class="mdi mdi-phone-outline"></i> ${company?.mobile || ''}</p>
              <p><i class="mdi mdi-email-outline"></i> ${company?.email || ''}</p>
              ${company?.website ? `<p><i class="mdi mdi-web"></i> ${company.website}</p>` : ''}
            </div>
            <div class="logo-container">
              ${company?.logo_thumb ? `<img src="${fileServerUrl}${company.logo_thumb}" alt="Logo" class="img-fluid">` : ''}
            </div>
          </div>
          <h4 class="text-center my-4"><strong>SHIFT-WISE ATTENDANCE REPORT</strong></h4>
          <div class="row mb-3">
            <div class="col-sm-8">
              <p><strong>Period:</strong> ${formatDateView(filters.from_date)} to ${formatDateView(filters.to_date)}</p>
              ${selectedShift ? `<p><strong>Shift:</strong> ${selectedShift.shift_name || selectedShift.name || ''}${selectedShift.code ? ` (${selectedShift.code})` : ''}</p>` : ''}
              ${selectedEmployee ? `<p><strong>Employee:</strong> ${selectedEmployee.name || ''} ${selectedEmployee.employee_code ? `(${selectedEmployee.employee_code})` : ''}</p>` : ''}
            </div>
            <div class="col-sm-4 text-end">
              <p><strong>Total Records:</strong> ${reportData.value.length}</p>
            </div>
          </div>
          <table class="table mt-4">
            <thead>
              <tr>
                <th>SN</th>
                <th>Date</th>
                <th>Shift</th>
                <th class="text-center">Total Empl.</th>
                <th class="text-center">Present</th>
                <th class="text-center">Absent</th>
                <th class="text-center">Late</th>
                <th class="text-center">Early</th>
                <th class="text-center">Late & Early</th>
                <th class="text-center">On Leave</th>
                <th class="text-center">Weekend/Holiday</th>
                <th class="text-center">Punch Missing</th>
                <th class="text-center">OT (hrs)</th>
              </tr>
            </thead>
            <tbody>
              ${reportData.value.map((row, index) => `
                <tr>
                  <td>${index + 1}</td>
                  <td>${formatDateView(row.date)}</td>
                  <td>${formatShift(row.shift)}</td>
                  <td class="text-center">${formatNumber(row.total_employees)}</td>
                  <td class="text-center">${formatNumber(row.present)}</td>
                  <td class="text-center">${formatNumber(row.absent)}</td>
                  <td class="text-center">${formatNumber(row.late)}</td>
                  <td class="text-center">${formatNumber(row.early)}</td>
                  <td class="text-center">${formatNumber(row.late_and_early)}</td>
                  <td class="text-center">${formatNumber(row.on_leave)}</td>
                  <td class="text-center">${formatNumber(row.weekend_holiday)}</td>
                  <td class="text-center">${formatNumber(row.punch_missing)}</td>
                  <td class="text-center">${formatNumber(row.total_ot_hours, 2)}</td>
                </tr>
              `).join('')}
              <tr class="totals-row">
                <td colspan="3" class="text-end">Total:</td>
                <td class="text-center">${formatNumber(totals.value.total_employees)}</td>
                <td class="text-center">${formatNumber(totals.value.present)}</td>
                <td class="text-center">${formatNumber(totals.value.absent)}</td>
                <td class="text-center">${formatNumber(totals.value.late)}</td>
                <td class="text-center">${formatNumber(totals.value.early)}</td>
                <td class="text-center">${formatNumber(totals.value.late_and_early)}</td>
                <td class="text-center">${formatNumber(totals.value.on_leave)}</td>
                <td class="text-center">${formatNumber(totals.value.weekend_holiday)}</td>
                <td class="text-center">${formatNumber(totals.value.punch_missing)}</td>
                <td class="text-center">${formatNumber(totals.value.total_ot_hours, 2)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </body>
    </html>
  `)
  printWindow.moveTo(0, 0)
  printWindow.focus()
  await new Promise(resolve => setTimeout(resolve, 500))
  printWindow.print()
  printWindow.close()
}

// Initialize
onMounted(async () => {
  await loadEmployees()
  await loadShifts()
  setDefaultDates()
})
</script>

<style scoped>
.v-card-title {
  font-weight: 600;
}

.table-bordered :deep(th),
.table-bordered :deep(td) {
  border: 1px solid #000000 !important;
}

.table-bordered :deep(table) {
  border-collapse: collapse;
}

.gap-2 {
  gap: 8px;
}

.panel-header {
  font-weight: 600;
}
</style>

